import * as THREE from 'three'
import type {
  AnimationClipData,
  AnimationInterpolation,
  AnimationLoopMode,
  AnimationTrackData,
  AnimationValueType,
} from '@/shared/types/animation'
import { applyAnimationEasing } from './easing'

export interface AnimationSampleOptions {
  duration?: number
  loopMode?: AnimationLoopMode
}

const VALUE_SIZES: Record<AnimationValueType, number> = {
  number: 1,
  vec2: 2,
  vec3: 3,
  vec4: 4,
  color: 3,
  quaternion: 4,
}

interface SplineCacheEntry {
  track: AnimationTrackData
  duration: number | undefined
  loopMode: AnimationLoopMode | undefined
  times: number[]
  knots: number[]
  dim: number
}

const splineCache = new WeakMap<AnimationTrackData, SplineCacheEntry>()

export function getAnimationValueSize(valueType: AnimationValueType): number {
  return VALUE_SIZES[valueType]
}

export function getClipDuration(clip: AnimationClipData): number {
  if (clip.duration !== undefined) {
    return clip.duration
  }
  let duration = 0
  for (const track of clip.tracks) {
    const last = track.times[track.times.length - 1] ?? 0
    duration = Math.max(duration, last)
  }
  return duration
}

export function normalizeAnimationTime(
  time: number,
  duration: number,
  loopMode: AnimationLoopMode = 'once'
): { time: number; completed: boolean } {
  if (duration <= 0) {
    return { time: 0, completed: true }
  }

  if (loopMode === 'repeat') {
    const wrapped = ((time % duration) + duration) % duration
    return { time: wrapped, completed: false }
  }

  if (loopMode === 'pingpong') {
    const span = duration * 2
    const wrapped = ((time % span) + span) % span
    return {
      time: wrapped <= duration ? wrapped : span - wrapped,
      completed: false,
    }
  }

  if (time <= 0) {
    return { time: 0, completed: time < 0 }
  }
  if (time >= duration) {
    return { time: duration, completed: true }
  }
  return { time, completed: false }
}

export function sampleAnimationTrack(
  track: AnimationTrackData,
  time: number,
  options: AnimationSampleOptions = {},
  target: number[] = []
): number[] {
  const interpolation = track.interpolation ?? 'linear'
  if (interpolation === 'spline') {
    return sampleSpline(track, time, options, target)
  }
  return sampleKeyframes(track, time, interpolation, target)
}

function sampleKeyframes(
  track: AnimationTrackData,
  time: number,
  interpolation: AnimationInterpolation,
  target: number[]
): number[] {
  const times = track.times
  const values = track.values
  const stride = getAnimationValueSize(track.valueType)
  target.length = stride

  if (times.length === 1 || time <= times[0]) {
    copySample(values, 0, stride, target)
    normalizeQuaternionResult(track, target)
    return target
  }

  const last = times.length - 1
  if (time >= times[last]) {
    copySample(values, last, stride, target)
    normalizeQuaternionResult(track, target)
    return target
  }

  const segment = findSegment(times, time)
  if (interpolation === 'step') {
    copySample(values, segment, stride, target)
    normalizeQuaternionResult(track, target)
    return target
  }

  const t0 = times[segment]
  const t1 = times[segment + 1]
  const rawProgress = (time - t0) / (t1 - t0)
  const easing = track.segmentEasing?.[segment] ?? track.easing
  const progress = applyAnimationEasing(rawProgress, easing)

  if (track.valueType === 'quaternion') {
    THREE.Quaternion.slerpFlat(
      target,
      0,
      values as number[],
      segment * stride,
      values as number[],
      (segment + 1) * stride,
      progress
    )
    normalizeQuaternion(target)
    return target
  }

  const offset0 = segment * stride
  const offset1 = (segment + 1) * stride
  for (let i = 0; i < stride; i++) {
    target[i] = values[offset0 + i] * (1 - progress) + values[offset1 + i] * progress
  }
  return target
}

function sampleSpline(
  track: AnimationTrackData,
  time: number,
  options: AnimationSampleOptions,
  target: number[]
): number[] {
  const entry = getSplineCacheEntry(track, options)
  const { times, knots, dim } = entry
  target.length = dim

  if (times.length === 0) {
    target.fill(0)
    return target
  }

  if (time <= times[0] || times.length === 1) {
    copyKnot(knots, dim, 0, target)
    return target
  }

  const last = times.length - 1
  if (time >= times[last]) {
    copyKnot(knots, dim, last, target)
    return target
  }

  const segment = findSegment(times, time)
  if (isSameSplineKnotValue(knots, dim, segment)) {
    copyKnot(knots, dim, segment, target)
    return target
  }

  const rawProgress = (time - times[segment]) / (times[segment + 1] - times[segment])
  const easing = track.segmentEasing?.[segment] ?? track.easing
  const t = applyAnimationEasing(rawProgress, easing)
  evaluateSplineSegment(knots, dim, segment, t, target)
  return target
}

function getSplineCacheEntry(
  track: AnimationTrackData,
  options: AnimationSampleOptions
): SplineCacheEntry {
  const cached = splineCache.get(track)
  if (cached && cached.duration === options.duration && cached.loopMode === options.loopMode) {
    return cached
  }

  const smoothness = track.smoothness ?? 1
  const baseTimes = Array.from(track.times)
  const basePoints = Array.from(track.values)
  const useLooping =
    options.loopMode === 'repeat' && options.duration !== undefined && baseTimes.length > 1
  const { times, points } = useLooping
    ? createLoopingPoints(options.duration!, baseTimes, basePoints)
    : { times: baseTimes, points: basePoints }
  const knots = calcSplineKnots(times, points, smoothness)
  const entry = {
    track,
    duration: options.duration,
    loopMode: options.loopMode,
    times,
    knots,
    dim: times.length > 0 ? points.length / times.length : 0,
  }
  splineCache.set(track, entry)
  return entry
}

function createLoopingPoints(
  duration: number,
  times: number[],
  points: number[]
): { times: number[]; points: number[] } {
  if (times.length < 2) {
    return { times: [...times], points: [...points] }
  }
  const dim = points.length / times.length
  const newTimes = times.slice()
  const newPoints = points.slice()
  newTimes.push(duration + times[0], duration + times[1])
  newPoints.push(...points.slice(0, dim * 2))
  newTimes.splice(0, 0, times[times.length - 2] - duration, times[times.length - 1] - duration)
  newPoints.splice(0, 0, ...points.slice(points.length - dim * 2))
  return { times: newTimes, points: newPoints }
}

function calcSplineKnots(times: number[], points: number[], smoothness: number): number[] {
  const n = times.length
  if (n === 0) return []
  const dim = points.length / n
  const knots = new Array<number>(n * dim * 3).fill(0)
  if (n === 1) {
    for (let j = 0; j < dim; j++) {
      knots[j * 3 + 1] = points[j]
    }
    return knots
  }

  for (let i = 0; i < n; i++) {
    const t = times[i]
    for (let j = 0; j < dim; j++) {
      const idx = i * dim + j
      const p = points[idx]
      let tangent: number
      if (isAdjacentDuplicatePoint(points, dim, i)) {
        tangent = 0
      } else if (i === 0) {
        tangent = (points[idx + dim] - p) / (times[i + 1] - t)
      } else if (i === n - 1) {
        tangent = (p - points[idx - dim]) / (t - times[i - 1])
      } else {
        tangent = (points[idx + dim] - points[idx - dim]) / (times[i + 1] - times[i - 1])
      }
      const inScale = i > 0 ? times[i] - times[i - 1] : times[1] - times[0]
      const outScale = i < n - 1 ? times[i + 1] - times[i] : times[i] - times[i - 1]
      knots[idx * 3] = tangent * inScale * smoothness
      knots[idx * 3 + 1] = p
      knots[idx * 3 + 2] = tangent * outScale * smoothness
    }
  }
  return knots
}

function isAdjacentDuplicatePoint(points: number[], dim: number, index: number): boolean {
  return (
    (index > 0 && isSamePoint(points, dim, index, index - 1)) ||
    (index < points.length / dim - 1 && isSamePoint(points, dim, index, index + 1))
  )
}

function isSamePoint(
  points: number[],
  dim: number,
  leftIndex: number,
  rightIndex: number
): boolean {
  const leftOffset = leftIndex * dim
  const rightOffset = rightIndex * dim
  for (let i = 0; i < dim; i++) {
    if (Math.abs(points[leftOffset + i] - points[rightOffset + i]) > Number.EPSILON) {
      return false
    }
  }
  return true
}

function evaluateSplineSegment(
  knots: number[],
  dim: number,
  segment: number,
  t: number,
  target: number[]
): void {
  const t2 = t * t
  const twot = t + t
  const omt = 1 - t
  const omt2 = omt * omt
  let idx = segment * dim * 3

  for (let i = 0; i < dim; i++) {
    const p0 = knots[idx + 1]
    const m0 = knots[idx + 2]
    const m1 = knots[idx + dim * 3]
    const p1 = knots[idx + dim * 3 + 1]
    target[i] =
      p0 * ((1 + twot) * omt2) + m0 * (t * omt2) + p1 * (t2 * (3 - twot)) + m1 * (t2 * (t - 1))
    idx += 3
  }
}

function isSameSplineKnotValue(knots: number[], dim: number, segment: number): boolean {
  const currentOffset = segment * dim * 3
  const nextOffset = (segment + 1) * dim * 3
  for (let i = 0; i < dim; i++) {
    const valueDelta = Math.abs(knots[currentOffset + i * 3 + 1] - knots[nextOffset + i * 3 + 1])
    if (valueDelta > Number.EPSILON) {
      return false
    }
  }
  return true
}

function findSegment(times: ArrayLike<number>, time: number): number {
  let left = 0
  let right = times.length - 2
  while (left <= right) {
    const mid = (left + right) >> 1
    if (time < times[mid]) {
      right = mid - 1
    } else if (time >= times[mid + 1]) {
      left = mid + 1
    } else {
      return mid
    }
  }
  return Math.max(0, Math.min(times.length - 2, left))
}

function copySample(
  values: ArrayLike<number>,
  index: number,
  stride: number,
  target: number[]
): void {
  const offset = index * stride
  for (let i = 0; i < stride; i++) {
    target[i] = values[offset + i]
  }
}

function copyKnot(knots: number[], dim: number, index: number, target: number[]): void {
  const offset = index * dim * 3
  for (let i = 0; i < dim; i++) {
    target[i] = knots[offset + i * 3 + 1]
  }
}

function normalizeQuaternionResult(track: AnimationTrackData, target: number[]): void {
  if (track.valueType === 'quaternion') {
    normalizeQuaternion(target)
  }
}

function normalizeQuaternion(values: number[]): void {
  const length = Math.hypot(values[0], values[1], values[2], values[3])
  if (length <= Number.EPSILON) {
    values[0] = 0
    values[1] = 0
    values[2] = 0
    values[3] = 1
    return
  }
  values[0] /= length
  values[1] /= length
  values[2] /= length
  values[3] /= length
}
